import { format, parseISO } from 'date-fns';
import React, { forwardRef, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LeadingActions,
  SwipeAction,
  SwipeableList,
  SwipeableListItem,
} from 'react-swipeable-list';
import useHover from '../../hooks/useHover';
import 'react-swipeable-list/dist/styles.css';

import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SkeletonTheme from 'react-loading-skeleton';
import { useShareForwardedRef } from '../../hooks/refs.hooks';
import { usePlaceImgUrlsForCard } from '../../hooks/trip/trip.hooks';
import useInViewport from '../../hooks/useInViewport';
import useMobileMediaQuery from '../../hooks/useMobileMediaQuery';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectCurrentTrip } from '../../redux/trip/trip.selectors';
import { removeCardFromList } from '../../redux/trip/trip.slice';
import { TripCard } from '../../redux/trip/trip.types';
import { getOpenStatus } from '../../utils/Card';
import { textColorForBg } from '../../utils/color.utils';
import { getNotesPreviewText, isNotesEmpty } from '../../utils/slate.utils';
import CardDetailsModal from '../CardDetailsModal/CardDetailsModal';
import CardActionBar from './CardActionBar/CardActionBar';
import CardDeleteConfirmationDialog from './CardDeleteConfirmationDialog/CardDeleteConfirmationDialog';
import CardTags from './CardTags/CardTags';
import CoverPhotoModal from './CoverPhotoModal/CoverPhotoModal';
import InlineRename from './InlineRename/InlineRename';
import './Card.scss';
import ReactionCount from './ReactionCount/ReactionCount';

export interface CardProps {
  card: TripCard;
  listId: string;
  tripId: string;
  cardOrder: string[];
  isLarge?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  onCardMouseEnter?: (card: TripCard) => void;
  onCardMouseLeave?: (card: TripCard) => void;
  isItinerarySidebarCard?: boolean;
  isPlaygroundCard?: boolean;
  listColor?: string;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      card,
      listId,
      tripId,
      cardOrder,
      isLarge = false,
      canEdit = false,
      canDelete = false,
      onCardMouseEnter,
      onCardMouseLeave,
      isItinerarySidebarCard = false,
      isPlaygroundCard = false,
      listColor,
    },
    forwardedRef
  ) => {
    const dispatch = useAppDispatch();
    const currentTrip = useAppSelector(selectCurrentTrip);
    const isMobile = useMobileMediaQuery();
    const [searchParams, setSearchParams] = useSearchParams();
    const [open, setOpen] = useState(false);
    const { isHovered, hoverProps } = useHover({
      onEnter: () => onCardMouseEnter?.(card),
      onLeave: () => onCardMouseLeave?.(card),
    });
    const [swipeProgress, setSwipeProgress] = useState(0);
    const [isRenamingCard, setIsRenamingCard] = useState(false);
    const [isCoverPhotoModalOpen, setIsCoverPhotoModalOpen] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const openStatus = useMemo(() => getOpenStatus(card.openingHours), [card.openingHours]);
    const notesPreview = useMemo(
      () => (!isNotesEmpty(card) && card.notes ? getNotesPreviewText(card.notes) : null),
      [card]
    );
    const listTextColor = useMemo(
      () => (listColor ? textColorForBg(listColor) : undefined),
      [listColor]
    );

    const scheduledDays = useMemo(() => {
      return Object.values(currentTrip?.events ?? {})
        .filter((e) => e.cardId === card.id && e.startDateISO)
        .map((e) => format(parseISO(e.startDateISO), 'EEEE, MMMM d'))
        .sort();
    }, [currentTrip?.events, card.id]);

    const sharedRef = useShareForwardedRef<HTMLDivElement>(forwardedRef);
    const { ref: viewportRef, isInViewport } = useInViewport();
    const { imgUrls, isLoading, onImgError } = usePlaceImgUrlsForCard(tripId, card, isInViewport, {
      coverOnly: true,
    });
    const mapId = '5f2483670fd46e10';

    // Combine refs for both shared functionality and viewport detection
    // Define this helper function outside your component
    function setRef<T>(ref: React.Ref<T>, value: T) {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref && typeof ref === 'object') {
        (ref as React.MutableRefObject<T>).current = value;
      }
    }

    // Use sharedRef for existing functionality that expects it
    const ref = sharedRef;

    // sets image source to Static Google Map or the resolved gateway photo
    const imgUrl =
      imgUrls.length > 0
        ? imgUrls[0]
        : `https://maps.googleapis.com/maps/api/staticmap?center=${card?.location?.latitude},${
            card?.location?.longitude
          }&zoom=15&size=${
            isLarge ? '200x200&scale=4' : '100x100&scale=2'
          }&maptype=roadmap&markers=size:mid%7Ccolor:red%7Clabel:o%7C${card?.location?.latitude},${
            card?.location?.longitude
          }&map_id=${mapId}&key=${process.env.REACT_APP_GOOGLE_API_KEY!}`;

    // biome-ignore lint/correctness/useExhaustiveDependencies: setRef rerenders
    const combinedRef = useCallback(
      (node: HTMLDivElement | null) => {
        setRef(sharedRef, node);
        setRef(viewportRef, node);
      },
      [sharedRef, viewportRef]
    );

    const handleOpen = () => {
      searchParams.set('card', card.id);
      searchParams.set('card-tab', !isNotesEmpty(card) ? '1' : '2');
      setSearchParams(searchParams);
    };
    const handleClose = () => {
      searchParams.delete('card');
      searchParams.delete('card-tab');
      setSearchParams(searchParams);
    };

    // Handle url navigation
    useEffect(() => {
      setOpen(searchParams.get('card') === card.id);
    }, [searchParams, card.id]);

    // handles delete inside card-details-mobile options menu
    const handleDeleteClick = (event: MouseEvent<EventTarget>) => {
      event.stopPropagation();

      // Show confirmation dialog if card has notes or is scheduled in itinerary
      if (!isNotesEmpty(card) || scheduledDays.length > 0) {
        setShowDeleteDialog(true);
      } else {
        // Delete immediately if no notes
        performDelete();
      }
    };

    // handles swipe-to-delete
    const handleSwipeProgress = (progress: number) => {
      setSwipeProgress(progress);
    };

    const handleSwipeToDelete = () => {
      // Show confirmation dialog if card has notes or is scheduled in itinerary
      if (!isNotesEmpty(card) || scheduledDays.length > 0) {
        setShowDeleteDialog(true);
      } else {
        // Delete immediately if no notes
        performSwipeDelete();
      }
    };

    // Common delete logic
    const performDelete = () => {
      setOpen(false);
      dispatch(
        removeCardFromList({
          tripId,
          listId,
          cardId: card.id,
          cardOrder,
        })
      );
    };

    // Swipe delete with animation
    const performSwipeDelete = () => {
      const cardContainer = ref.current;
      if (cardContainer) {
        cardContainer.style.transition = 'opacity 0.5s ease';
        cardContainer.style.opacity = '0';
        setTimeout(() => {
          performDelete();
        }, 250); // Wait for the animation to complete before actually removing the card
      } else {
        performDelete();
      }
    };

    // Confirmation dialog handlers
    const handleConfirmDelete = () => {
      setShowDeleteDialog(false);
      performDelete();
    };

    const handleCancelDelete = () => {
      setShowDeleteDialog(false);
    };

    // Action handlers for new action bar
    const handleRename = () => {
      setIsRenamingCard(true);
    };

    const handleRenameComplete = () => {
      setIsRenamingCard(false);
    };

    const handleChangeCoverPhoto = () => {
      setIsCoverPhotoModalOpen(true);
    };

    const handleCloseCoverPhotoModal = () => {
      setIsCoverPhotoModalOpen(false);
    };

    const leadingActions = () => (
      <LeadingActions>
        <SwipeAction onClick={handleSwipeToDelete}>
          <div className='swipe-to-delete-container'>
            <DeleteIcon
              className={`swipe-to-delete-icon${swipeProgress >= 50 ? ' large-icon' : ''}`}
            />
          </div>
        </SwipeAction>
      </LeadingActions>
    );

    return (
      <>
        {/* ============================================================
            MOBILE CARD 
            Variants: small (default) · large (isLarge) · itinerary-sidebar (isItinerarySidebarCard)
            ============================================================ */}
        {isMobile && canEdit ? (
          <SwipeableList
            style={{
              width: '98%',
              borderRadius: '10px',
              MozBoxShadow: '1px 1px 4px 2px #ccc',
              WebkitBoxShadow: '1px 1px 4px 2px #ccc',
              boxShadow: '1px 1px 4px 2px #ccc',
            }}
          >
            <SwipeableListItem
              leadingActions={leadingActions()}
              threshold={0.5}
              onSwipeProgress={handleSwipeProgress}
            >
              <div
                ref={combinedRef}
                className={[
                  'card-container',
                  isLarge ? 'large' : '',
                  isItinerarySidebarCard ? 'itinerary-sidebar' : '',
                  notesPreview ? 'has-notes' : '',
                  isMobile && canEdit ? 'mobile can-edit' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={listColor ? { backgroundColor: listColor } : undefined}
                onClick={handleOpen}
                {...hoverProps}
              >
                {/* Don't show action bar on mobile or in itinerary sidebar */}
                {!isMobile && !isItinerarySidebarCard && (
                  <CardActionBar
                    card={card}
                    tripId={tripId}
                    canEdit={canEdit}
                    isVisible={isHovered}
                    isLarge={isLarge}
                    onRename={handleRename}
                    onChangeCoverPhoto={handleChangeCoverPhoto}
                    onDelete={handleDeleteClick}
                  />
                )}
                {/* Show drag handle on hover for drag and drop feedback */}
                {canEdit && isPlaygroundCard && !isMobile && (
                  <div
                    className={[
                      'drag-handle-container',
                      isLarge ? 'large' : '',
                      isHovered ? 'visible' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <DragIndicatorIcon className='drag-handle' />
                  </div>
                )}
                <div className={`card-img-container${isLarge ? ' large' : ''}`}>
                  {isLoading ? (
                    <SkeletonTheme className='card-img-skeleton' />
                  ) : (
                    <img
                      src={imgUrl}
                      className={`card-img${isLarge ? ' large' : ''}`}
                      onError={imgUrls.length ? onImgError : undefined}
                    />
                  )}
                  {/* Place ReactionCount inside image container for large cards */}
                  {isLarge && (
                    <ReactionCount
                      card={card}
                      isLarge={isLarge}
                      isHovered={isHovered}
                      isMobile={isMobile}
                      isItinerarySidebarCard={isItinerarySidebarCard}
                    />
                  )}
                </div>
                <div
                  className={`card-description-container${isLarge ? ' large' : ''}`}
                  style={listTextColor ? { color: listTextColor } : undefined}
                >
                  <CardTags
                    card={card}
                    isLarge={isLarge}
                    isItinerarySidebarCard={isItinerarySidebarCard}
                  />
                  <h3 className={`card-name${isItinerarySidebarCard ? ' itinerary-sidebar' : ''}`}>
                    {((card.customName ?? card.name) || '').length > 57
                      ? `${((card.customName ?? card.name) || '').substring(0, 57)}...`
                      : (card.customName ?? card.name)}
                  </h3>
                  {notesPreview && <p className='card-notes-preview'>{notesPreview}</p>}
                  {openStatus && (
                    <span className='card-open-status'>
                      {openStatus.isOpen ? (
                        openStatus.label
                      ) : (
                        <>
                          <span className='card-open-status__closed'>Closed</span>
                          {openStatus.nextOpenTime && ` Opens ${openStatus.nextOpenTime}`}
                        </>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </SwipeableListItem>
          </SwipeableList>
        ) : (
          // ============================================================
          // STANDARD CARD
          // Variants: small (default) · large (isLarge) · itinerary-sidebar (isItinerarySidebarCard)
          // ============================================================
          <div
            ref={combinedRef}
            className={[
              'card-container',
              isLarge ? 'large' : '',
              isItinerarySidebarCard ? 'itinerary-sidebar' : '',
              notesPreview ? 'has-notes' : '',
              isMobile && canEdit ? 'mobile can-edit' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={listColor ? { backgroundColor: listColor } : undefined}
            onClick={handleOpen}
            {...hoverProps}
          >
            {/* Don't show action bar in itinerary sidebar */}
            {!isItinerarySidebarCard && (
              <CardActionBar
                card={card}
                tripId={tripId}
                canEdit={canEdit}
                isVisible={isHovered}
                isLarge={isLarge}
                onRename={handleRename}
                onChangeCoverPhoto={handleChangeCoverPhoto}
                onDelete={handleDeleteClick}
              />
            )}
            {/* Show drag handle on hover for drag and drop feedback */}
            {canEdit && isPlaygroundCard && !isMobile && (
              <div
                className={[
                  'drag-handle-container',
                  isLarge ? 'large' : '',
                  isHovered ? 'visible' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <DragIndicatorIcon className={`drag-handle${isLarge ? ' large' : ''}`} />
              </div>
            )}
            <div className={`card-img-container${isLarge ? ' large' : ''}`}>
              {isLoading ? (
                <SkeletonTheme className='card-img-skeleton' />
              ) : (
                <img
                  src={imgUrl}
                  className={`card-img${isLarge ? ' large' : ''}`}
                  onError={imgUrls.length ? onImgError : undefined}
                />
              )}
              {/* Place ReactionCount inside image container for large cards */}
              {isLarge && (
                <ReactionCount
                  card={card}
                  isLarge={isLarge}
                  isHovered={isHovered}
                  isMobile={isMobile}
                  isItinerarySidebarCard={isItinerarySidebarCard}
                />
              )}
            </div>
            {/* For non-large cards, keep ReactionCount outside image container */}
            {!isLarge && (
              <ReactionCount
                card={card}
                isLarge={isLarge}
                isHovered={isHovered}
                isMobile={isMobile}
                isItinerarySidebarCard={isItinerarySidebarCard}
              />
            )}
            <div
              className={`card-description-container${isLarge ? ' large' : ''}`}
              style={listTextColor ? { color: listTextColor } : undefined}
            >
              <CardTags
                card={card}
                isLarge={isLarge}
                isItinerarySidebarCard={isItinerarySidebarCard}
              />
              <h3
                className={`card-name${isItinerarySidebarCard ? ' itinerary-sidebar' : ''}`}
                style={{ opacity: isRenamingCard ? 0 : 1 }}
              >
                {((card.customName ?? card.name) || '').length > 57
                  ? `${((card.customName ?? card.name) || '').substring(0, 57)}...`
                  : (card.customName ?? card.name)}
              </h3>
              {notesPreview && <p className='card-notes-preview'>{notesPreview}</p>}
              {openStatus && (
                <span className='card-open-status'>
                  {openStatus.isOpen ? (
                    openStatus.label
                  ) : (
                    <>
                      <span className='card-open-status__closed'>Closed</span>
                      {openStatus.nextOpenTime && ` Opens ${openStatus.nextOpenTime}`}
                    </>
                  )}
                </span>
              )}
            </div>

            {/* Inline Rename Overlay - positioned within this specific card */}
            <InlineRename
              card={card}
              tripId={tripId}
              isActive={isRenamingCard}
              isLarge={isLarge}
              onComplete={handleRenameComplete}
            />
          </div>
        )}
        {open && (
          <CardDetailsModal
            card={card}
            tripId={tripId}
            canEdit={canEdit}
            open={open}
            handleClose={handleClose}
            handleDeleteClick={handleDeleteClick}
          />
        )}

        {/* Cover Photo Modal */}
        <CoverPhotoModal
          card={card}
          tripId={tripId}
          isOpen={isCoverPhotoModalOpen}
          onClose={handleCloseCoverPhotoModal}
        />

        {/* Delete Confirmation Dialog */}
        <CardDeleteConfirmationDialog
          card={card}
          open={showDeleteDialog}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          scheduledDays={scheduledDays}
        />
      </>
    );
  }
);

Card.displayName = 'Card';
export default Card;
